import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { X } from 'lucide-react';
import { LANG_CODES, getLangCode } from '../types/Show';

// Generate languages array from comprehensive enum
const languages = Object.values(LANG_CODES).map(code => ({
  value: code,
  label: getLangCode(code)
}));

const GENDER_OPTIONS = ['MALE', 'FEMALE'];

interface InternalDetailsData {
  actorEthnicity: string;
  sourceSubtitleLanguage: string;
  trailerLinks: string[];
  leadCharacterGender: string;
  targetAudienceGender: string;
  sourceLanguage: string;
  scrapingLink?: string;
}

interface InternalDetailsFormProps {
  data: InternalDetailsData;
  onChange: (data: InternalDetailsData) => void;
}

export const InternalDetailsForm: React.FC<InternalDetailsFormProps> = ({ data, onChange }) => {
  // Ensure data has the expected structure with defaults
  const safeData = data || {
    actorEthnicity: '',
    sourceSubtitleLanguage: '',
    trailerLinks: [],
    leadCharacterGender: '',
    targetAudienceGender: '',
    sourceLanguage: '',
    scrapingLink: ''
  };
  
  const [newTrailerLink, setNewTrailerLink] = useState('');

  const updateField = (field: keyof InternalDetailsData, value: any) => {
    onChange({ ...safeData, [field]: value });
  };

  const addTrailerLink = () => {
    if (newTrailerLink.trim() && !safeData.trailerLinks.includes(newTrailerLink.trim())) {
      updateField('trailerLinks', [...safeData.trailerLinks, newTrailerLink.trim()]);
      setNewTrailerLink('');
    }
  };

  const removeTrailerLink = (index: number) => {
    updateField('trailerLinks', safeData.trailerLinks.filter((_, i) => i !== index));
  };

  const handleTrailerLinkKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTrailerLink();
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Internal Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Actor Ethnicity */}
              <div className="space-y-2">
                <Label htmlFor="actorEthnicity">Actor Ethnicity</Label>
                <Input
                  id="actorEthnicity"
                  placeholder="Enter actor ethnicity"
                  value={safeData.actorEthnicity}
                  onChange={(e) => updateField('actorEthnicity', e.target.value)}
                />
              </div>

              {/* Lead Character Gender */}
              <div className="space-y-2">
                <Label htmlFor="leadCharacterGender">Lead Character Gender</Label>
                <Select
                  value={safeData.leadCharacterGender}
                  onValueChange={(value) => updateField('leadCharacterGender', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select lead character gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Audience Gender */}
              <div className="space-y-2">
                <Label htmlFor="targetAudienceGender">Target Audience Gender</Label>
                <Select
                  value={safeData.targetAudienceGender}
                  onValueChange={(value) => updateField('targetAudienceGender', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select target audience gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Source Subtitle Language */}
              <div className="space-y-2">
                <Label htmlFor="sourceSubtitleLanguage">Source Subtitle Language</Label>
                <Select
                  value={safeData.sourceSubtitleLanguage}
                  onValueChange={(value) => updateField('sourceSubtitleLanguage', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source subtitle language" />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Trailer Links */}
              <div className="space-y-2">
                <Label htmlFor="trailerLinks">Trailer Links</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter trailer link URL"
                    value={newTrailerLink}
                    onChange={(e) => setNewTrailerLink(e.target.value)}
                    onKeyDown={handleTrailerLinkKeyPress}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTrailerLink}
                    disabled={!newTrailerLink.trim()}
                  >
                    Add
                  </Button>
                </div>
                {safeData.trailerLinks.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {safeData.trailerLinks.map((link, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        <span className="text-xs truncate max-w-[200px]">{link}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => removeTrailerLink(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Source Language */}
              <div className="space-y-2">
                <Label htmlFor="sourceLanguage">Source Language</Label>
                <Select
                    value={safeData.sourceLanguage}
                    onValueChange={(value) => updateField('sourceLanguage', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source language" />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                        <SelectItem key={language.value} value={language.value}>
                          {language.label}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scraping Link from Outsourced Show */}
      <Card>
        <CardHeader>
          <CardTitle>Outsourced Show Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="scrapingLink">Scraping Link</Label>
            <Input
              id="scrapingLink"
              value={safeData.scrapingLink || ''}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
              placeholder="No scraping link available"
            />
            <p className="text-sm text-muted-foreground">
              This link is from the original outsourced show and is read-only.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};